import { Link } from "@tanstack/react-router";
import { LockKeyhole, Settings } from "lucide-react";
import { useTranslations } from "use-intl";
import type { FeatureCode } from "@/features/system-admin/constants/features";
import { FEATURE_LABEL_KEYS } from "../lib/feature-copy";
import { useCurrentOrg } from "@/stores/useOrgStore";
import { useIsSystemRoot } from "@/features/system-admin/hooks/use-system-role";

interface FeatureDisabledCardProps {
  feature: FeatureCode;
}

export function FeatureDisabledCard({ feature }: FeatureDisabledCardProps) {
  const t = useTranslations("features");
  const currentOrg = useCurrentOrg();
  const isSystemRoot = useIsSystemRoot();
  const label = t(FEATURE_LABEL_KEYS[feature]);
  const orgName = currentOrg?.name ?? t("disabled.current_org");

  return (
    <section className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-5">
      <div className="flex gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-[var(--muted)] text-[var(--muted-foreground)]">
          <LockKeyhole className="size-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 space-y-3">
          <div>
            <h2 className="text-base font-semibold text-[var(--foreground)]">
              {t("disabled.title", { feature: label })}
            </h2>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              {t("disabled.description", { feature: label, orgName })}
            </p>
          </div>
          {isSystemRoot && currentOrg ? (
            <Link
              to="/system-admin/orgs/$orgId"
              params={{ orgId: currentOrg.id }}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-[var(--border)] px-3 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--accent)]"
            >
              <Settings className="size-4" aria-hidden="true" />
              {t("disabled.manage_features")}
            </Link>
          ) : (
            <p className="text-xs font-medium text-[var(--muted-foreground)]">
              {t("disabled.system_admin_hint")}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
